import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Custom hook for scroll-triggered animations
 * Re-triggers animation each time the element enters the viewport
 * Respects user's reduced motion preference for accessibility
 *
 * @param {Object} options - Configuration options
 * @param {string} options.rootMargin - IntersectionObserver root margin (default: '-100px 0px')
 * @param {number} options.threshold - IntersectionObserver threshold (default: 0.1)
 * @param {number} options.delay - Delay before starting observer in ms (default: 100)
 * @returns {Object} { ref, isVisible } - Ref to attach to element and visibility state
 *
 * @example
 * const { ref, isVisible } = useScrollAnimation();
 * return <section ref={ref} className={isVisible ? 'is-visible' : ''}>...</section>
 */
const useScrollAnimation = ({
  rootMargin = '-100px 0px',
  threshold = 0.1,
  delay = 100
} = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef(null);

  // Check for reduced motion preference
  const prefersReducedMotion = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // If user prefers reduced motion, always show content without animation
    if (prefersReducedMotion()) {
      setIsVisible(true);
      return;
    }

    // Delay observer start to ensure initial render completes with hidden state
    // This prevents animation from firing immediately on page load
    const timeoutId = setTimeout(() => {
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          // Update visibility based on intersection state
          // Animation triggers on enter and resets on exit
          setIsVisible(entry.isIntersecting);
        },
        {
          root: null,
          rootMargin,
          threshold
        }
      );

      observerRef.current.observe(element);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [rootMargin, threshold, delay, prefersReducedMotion]);

  return { ref, isVisible };
};

export default useScrollAnimation;
